import { useEffect } from 'react'
import { useCompanionManager, assessTranscript } from '@edge/edge-ai'
import type { CompanionManager } from '@edge/edge-ai'
import { useVoiceStore, type VoiceSessionState } from '@/stores/voice-store'
import { useChatStore } from '@/stores/chat-store'
import { useLanguageStore } from '@/stores/language-store'
import { followSpanishQuestion } from '@/features/companion/language-follow'
import {
  createDictationService,
  type DictationResult,
  type DictationService,
} from './dictation-service'

/**
 * The ONE owner of composer-voice side effects (mic, transcription WS,
 * manager wiring). Mounted once by CompanionHost — the same single-mount rule
 * as useCompanionConversation; the package-side `useCompanion` (Ctrl-gesture
 * voice in CompanionOverlay) keeps its own separately-activated capture, and
 * only one of the two records at a time.
 *
 * Composer buttons never touch audio APIs — they call `voiceControls`, which
 * routes into the controller this hook installs. Before the hook mounts (or
 * in surfaces without it, e.g. the popout) the controls are no-ops.
 *
 * Voice-session replies ride the EXISTING pipeline: `manager.sendMessage` is
 * the voice path, whose answers are spoken by the already-mounted
 * `useCompanion` TTS subscription (or aura live-streaming via the
 * direct-analyst voice modality). Nothing here plays audio.
 */

type ServiceFactory = (apiBase: string, getAuthToken?: () => Promise<string>) => DictationService

/**
 * Silence after speech that ends a session turn and sends it. A flat 1.5 s
 * cut people off mid-thought (2026-10-02: "...and now the fact that it's
 * gapped up again" was sent on a thinking pause), so an unfinished thought
 * gets longer. Speech-to-text punctuates, so a finished sentence is visible.
 */
export const AUTO_SEND_SILENCE_MS = 1500
export const AUTO_SEND_SILENCE_UNFINISHED_MS = 3500

/** How long a pause may last before the turn is sent, given what was heard so far. */
export function autoSendDelay(transcript: string): number {
  const t = transcript.trim()
  return /[.?!]["')\]]?$/.test(t) ? AUTO_SEND_SILENCE_MS : AUTO_SEND_SILENCE_UNFINISHED_MS
}

/** Shown when a heard turn is dropped by the transcript gate (never silent). */
const DROPPED_TURN_NOTICE: Record<string, string> = {
  en: "Sorry, I didn't catch that. Try again.",
  es: 'No te entendí. Inténtalo de nuevo.',
}
const NOTICE_MS = 6000

class VoiceController {
  private service: DictationService | null = null
  private capture: 'dictation' | 'session' | null = null
  private onDictationFinal: ((text: string) => void) | null = null
  private unsubscribe: () => void
  private unsubscribeChat: () => void
  /** Endpointing: armed on each interim; firing = the user stopped talking. */
  private autoSendTimer: ReturnType<typeof setTimeout> | null = null
  /** Re-entry guard: startVoiceTurn's own manager.cancel() emits 'idle'. */
  private startingTurn = false
  private noticeTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private readonly manager: CompanionManager,
    private readonly serviceFactory: ServiceFactory,
  ) {
    // Mirror manager state into the store while a session is live — and keep
    // the conversation HANDS-FREE: when an answer finishes (processing/
    // responding → idle) with the session still open, listen again for the
    // follow-up automatically.
    let prev: VoiceSessionState = 'idle'
    this.unsubscribe = manager.events.on((event) => {
      if (event.type !== 'state-change') return
      if (useVoiceStore.getState().status !== 'voice-session') return
      const next = event.state as VoiceSessionState
      const answerJustFinished = next === 'idle' && (prev === 'processing' || prev === 'responding')
      prev = next
      useVoiceStore.getState().setSessionState(next)
      if (answerJustFinished && !this.capture && !this.startingTurn) {
        void this.startVoiceTurn()
      }
    })
    // Closing the companion (✕, Escape, launcher) or switching its surface
    // (pin to side / float) ends a live voice session: voice belongs to the
    // window it was started in (Chris, 2026-10-02).
    this.unsubscribeChat = useChatStore.subscribe((state, prev) => {
      if ((state.open !== prev.open && !state.open) || state.surface !== prev.surface) {
        this.endVoiceSession()
      }
    })
  }

  private showNotice(text: string): void {
    this.clearNotice()
    useVoiceStore.getState().setNotice(text)
    this.noticeTimer = setTimeout(() => this.clearNotice(), NOTICE_MS)
  }

  private clearNotice(): void {
    if (this.noticeTimer) clearTimeout(this.noticeTimer)
    this.noticeTimer = null
    if (useVoiceStore.getState().notice !== null) useVoiceStore.getState().setNotice(null)
  }

  private clearAutoSend(): void {
    if (this.autoSendTimer) clearTimeout(this.autoSendTimer)
    this.autoSendTimer = null
  }

  /** Each interim re-arms the silence timer; quiet after speech = send. */
  private bumpAutoSend(transcript: string): void {
    this.clearAutoSend()
    this.autoSendTimer = setTimeout(() => {
      this.autoSendTimer = null
      void this.stopVoiceTurn()
    }, autoSendDelay(transcript))
  }

  private buildService(): DictationService {
    return this.serviceFactory(this.manager.config.apiBase, this.manager.config.getAuthToken)
  }

  private async startCapture(kind: 'dictation' | 'session'): Promise<boolean> {
    const store = useVoiceStore.getState()
    this.service = this.buildService()
    this.capture = kind
    try {
      await this.service.start(
        (text) => {
          store.setInterimText(text)
          // Endpointing only once the user has actually said something —
          // initial silence keeps the mic open indefinitely (✕ ends it).
          if (kind === 'session' && text.trim()) this.bumpAutoSend(text)
        },
        (level) => store.setAudioLevel(level),
      )
      return true
    } catch {
      // Mic denied / connect failed — reset instead of a stuck button.
      this.service = null
      this.capture = null
      store.setAudioLevel(0)
      store.setInterimText('')
      return false
    }
  }

  private async stopCapture(): Promise<string> {
    return (await this.stopCaptureDetailed()).text
  }

  private async stopCaptureDetailed(): Promise<DictationResult> {
    this.clearAutoSend()
    const store = useVoiceStore.getState()
    const service = this.service
    this.service = null
    this.capture = null
    store.setAudioLevel(0)
    store.setInterimText('')
    if (!service) return { text: '' }
    try {
      return service.stopDetailed ? await service.stopDetailed() : { text: await service.stop() }
    } catch {
      return { text: '' }
    }
  }

  /** Mic button: click to dictate into the textarea, click again to finish. */
  async toggleDictation(onFinal: (text: string) => void): Promise<void> {
    const store = useVoiceStore.getState()
    if (store.status === 'voice-session') return
    if (store.status === 'dictating') {
      const text = await this.stopCapture()
      store.setStatus('idle')
      if (text) this.onDictationFinal?.(text)
      this.onDictationFinal = null
      return
    }
    this.onDictationFinal = onFinal
    store.setStatus('dictating')
    const ok = await this.startCapture('dictation')
    if (!ok) {
      store.setStatus('idle')
      this.onDictationFinal = null
    }
  }

  /** Speak pill: enter the spoken back-and-forth and start listening. */
  async startVoiceSession(): Promise<void> {
    const store = useVoiceStore.getState()
    if (store.status !== 'idle') return
    store.setStatus('voice-session')
    await this.startVoiceTurn()
  }

  /** In-session: begin a listening turn. */
  async startVoiceTurn(): Promise<void> {
    const store = useVoiceStore.getState()
    if (store.status !== 'voice-session' || this.capture || this.startingTurn) return
    this.startingTurn = true
    try {
      // Barge-in: a new turn cancels any in-flight answer/speech.
      this.manager.cancel()
      this.manager.startListening()
      store.setSessionState('listening')
      const ok = await this.startCapture('session')
      if (!ok) {
        this.manager.cancel()
        store.setSessionState('idle')
      }
    } finally {
      this.startingTurn = false
    }
  }

  /** In-session: stop listening and send what was heard. */
  async stopVoiceTurn(): Promise<void> {
    const store = useVoiceStore.getState()
    if (store.status !== 'voice-session' || this.capture !== 'session') return
    const { text, language, confidence } = await this.stopCaptureDetailed()
    // A Spanish question flips the option first, so the gate below expects
    // Spanish and this same turn is answered in it.
    followSpanishQuestion(text, this.manager)
    // Open-mic noise / garbled foreign-language fragments: no send, no credits,
    // no reply. A dropped (non-empty) turn re-opens the mic so the open
    // session keeps listening instead of going deaf.
    const verdict = assessTranscript({
      text,
      detectedLanguage: language,
      confidence,
      expectedLanguage: this.manager.detectedLanguage,
    })
    if (verdict.ok) {
      this.clearNotice()
      // The voice path — replies get spoken by the existing TTS pipeline.
      void this.manager.sendMessage(text)
    } else {
      if (verdict.reason !== 'empty') {
        console.debug('[voice] dropped transcript:', verdict.reason, JSON.stringify(text))
      }
      this.manager.cancel()
      store.setSessionState('idle')
      if (verdict.reason !== 'empty') {
        // Say so before the mic reopens, so a dropped question never looks
        // like the AI ignored the user.
        this.showNotice(
          DROPPED_TURN_NOTICE[useLanguageStore.getState().language] ?? DROPPED_TURN_NOTICE.en!,
        )
        void this.startVoiceTurn()
      }
    }
  }

  /** ✕ End: leave the session entirely. */
  endVoiceSession(): void {
    const store = useVoiceStore.getState()
    if (store.status !== 'voice-session') return
    this.clearAutoSend()
    this.clearNotice()
    this.service?.cancel()
    this.service = null
    this.capture = null
    this.manager.cancel()
    store.reset()
  }

  dispose(): void {
    this.clearAutoSend()
    this.clearNotice()
    this.service?.cancel()
    this.unsubscribe()
    this.unsubscribeChat()
    useVoiceStore.getState().reset()
  }
}

let controller: VoiceController | null = null

/** The composer's voice entry points — safe no-ops until the hook mounts. */
export const voiceControls = {
  toggleDictation(onFinal: (text: string) => void): void {
    void controller?.toggleDictation(onFinal)
  },
  startVoiceSession(): void {
    void controller?.startVoiceSession()
  },
  startVoiceTurn(): void {
    void controller?.startVoiceTurn()
  },
  stopVoiceTurn(): void {
    void controller?.stopVoiceTurn()
  },
  endVoiceSession(): void {
    controller?.endVoiceSession()
  },
}

/** Test seam: install a controller against a fake manager/service. */
export function __installControllerForTests(
  manager: CompanionManager,
  serviceFactory: ServiceFactory,
): () => void {
  controller = new VoiceController(manager, serviceFactory)
  return () => {
    controller?.dispose()
    controller = null
  }
}

export function useVoiceSession(): void {
  const manager = useCompanionManager()
  useEffect(() => {
    controller = new VoiceController(manager, createDictationService)
    // Stop any capture when the tab hides (mirrors VoicePTT).
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        const status = useVoiceStore.getState().status
        if (status === 'dictating') void controller?.toggleDictation(() => {})
        if (status === 'voice-session') controller?.endVoiceSession()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      controller?.dispose()
      controller = null
    }
  }, [manager])
}
